import React, { useEffect, useRef, useState } from 'react';
import { 
  FaClipboardList, FaFileAlt, FaMoneyBillWave, FaCheckCircle, 
  FaArrowRight, FaCalendarAlt, FaPhoneAlt, FaEnvelope,
  FaWhatsapp, FaGraduationCap, FaUserPlus, FaRocket, FaTimes,
  FaUser, FaBookOpen, FaUpload, FaCreditCard, FaIdCard, FaSpinner
} from 'react-icons/fa';
import { 
  MdAssignment, MdVerified, MdTimeline, MdSchool, MdEmail, MdPhone
} from 'react-icons/md';
import { GiArchiveResearch, GiBookCover } from 'react-icons/gi';

const AdmissionSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [modalOpen, setModalOpen] = useState(null); // 'apply', 'upload', 'pay', 'status'
  const [formData, setFormData] = useState({
    // Apply form
    fullName: '',
    email: '',
    phone: '',
    course: '',
    qualification: '',
    // Upload form
    documents: {
      marksheet10: null,
      marksheet12: null,
      photo: null,
      signature: null,
    },
    // Pay form
    paymentMethod: '',
    // Status form
    applicationId: '',
    mobileNumber: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [statusResult, setStatusResult] = useState(null);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e, docType) => {
    setFormData(prev => ({
      ...prev,
      documents: { ...prev.documents, [docType]: e.target.files[0] }
    }));
  };

  const handleApplySubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setTimeout(() => {
      setIsSubmitting(false);
      setModalOpen(null);
      alert('Application submitted successfully! You will receive a confirmation email shortly.');
    }, 1500);
  };

  const handleUploadSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setTimeout(() => {
      setIsSubmitting(false);
      setModalOpen(null);
      alert('Documents uploaded successfully! Our team will verify them within 24 hours.');
    }, 1500);
  };

  const handlePaySubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setTimeout(() => {
      setIsSubmitting(false);
      setModalOpen(null);
      alert('Payment initiated! You will be redirected to payment gateway.');
    }, 1500);
  };

  const handleStatusSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setTimeout(() => {
      setIsSubmitting(false);
      setStatusResult({
        status: 'Under Review',
        applicationId: formData.applicationId || 'APP2025001',
        name: 'John Doe',
        course: 'B.Sc Computer Science',
        date: '2025-04-15',
        message: 'Your application is being processed. You will receive an update within 3 business days.'
      });
    }, 1500);
  };

  const steps = [
    {
      number: "01",
      title: "Fill Application",
      icon: FaClipboardList,
      color: "from-blue-600 to-cyan-500",
      description: "Complete online application form with personal and academic details.",
      duration: "10-15 mins",
      action: "Apply Now",
      modalId: "apply"
    },
    {
      number: "02",
      title: "Submit Documents",
      icon: FaFileAlt,
      color: "from-green-500 to-emerald-500",
      description: "Upload required documents: marksheets, ID proof, photographs.",
      duration: "5-10 mins",
      action: "Upload Now",
      modalId: "upload"
    },
    {
      number: "03",
      title: "Pay Application Fee",
      icon: FaMoneyBillWave,
      color: "from-orange-500 to-red-500",
      description: "Pay ₹500 application fee via online banking, card, or UPI.",
      duration: "2-3 mins",
      action: "Pay Now",
      modalId: "pay"
    },
    {
      number: "04",
      title: "Get Confirmation",
      icon: FaCheckCircle,
      color: "from-blue-300 to-blue-400",
      description: "Receive admission confirmation and enrollment number.",
      duration: "24-48 hrs",
      action: "Check Status",
      modalId: "status"
    }
  ];

  const requirements = [
    { text: "10th & 12th Marksheets", icon: GiBookCover, color: "text-blue-600" },
    { text: "Transfer Certificate (TC)", icon: MdAssignment, color: "text-green-500" },
    { text: "Character Certificate", icon: MdVerified, color: "text-blue-300" },
    { text: "Caste Certificate (if applicable)", icon: GiArchiveResearch, color: "text-orange-500" },
    { text: "Passport Size Photos (4)", icon: FaUserPlus, color: "text-blue-400" },
    { text: "Aadhar Card Copy", icon: MdVerified, color: "text-indigo-500" }
  ];

  const dates = [
    { label: "Application Start", date: "April 1, 2025", status: "Open" },
    { label: "Last Date", date: "June 30, 2025", status: "Coming Soon" },
    { label: "Merit List", date: "July 15, 2024", status: "Upcoming" },
    { label: "Classes Start", date: "August 1, 2024", status: "Upcoming" }
  ];

  const courses = [
    "B.Sc Computer Science",
    "B.Com",
    "B.A. Economics",
    "BCA",
    "MBA",
    "MCA"
  ];

  // Modal Component
  const Modal = ({ isOpen, onClose, title, children }) => {
    if (!isOpen) return null;
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fadeIn">
        <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto shadow-2xl animate-slideUp">
          <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center rounded-t-2xl">
            <h3 className="text-xl font-bold text-gray-800">{title}</h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
              <FaTimes size={20} />
            </button>
          </div>
          <div className="p-6">
            {children}
          </div>
        </div>
      </div>
    );
  };

  return (
    <section ref={sectionRef} className="py-16 px-6 md:px-10 bg-gradient-to-b from-green-50 via-white to-indigo-50/30">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className={`text-center mb-10 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-800">
            Admission
            <span className="block bg-gradient-to-r from-blue-600 to-indigo-500 bg-clip-text text-transparent">
              Process 2025-26
            </span>
          </h2>
          <div className="w-16 h-0.5 bg-gradient-to-r from-blue-600 to-indigo-400 mx-auto rounded-full mt-3"></div>
          <p className="text-gray-600 text-sm max-w-2xl mx-auto mt-3">
            Simple, transparent, and hassle-free admission process
          </p>
        </div>

        {/* Steps Cards - 4 in a row */}
        <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-12 transition-all duration-700 delay-200 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          {steps.map((step, idx) => (
            <div 
              key={idx}
              className="group relative bg-white rounded-xl p-5 shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border border-gray-100"
              onMouseEnter={() => setActiveStep(idx)}
              onMouseLeave={() => setActiveStep(0)}
            >
              <div className="absolute top-3 right-3 text-3xl font-bold text-gray-100 group-hover:text-gray-200 transition-colors">
                {step.number}
              </div>
              <div className={`w-12 h-12 bg-gradient-to-br ${step.color} rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                <step.icon className="text-white text-xl" />
              </div>
              <h3 className="text-lg font-bold text-gray-800 mb-2">{step.title}</h3>
              <p className="text-gray-600 text-xs leading-relaxed mb-3">{step.description}</p>
              <div className="flex items-center gap-1 text-xs text-gray-400 mb-3">
                <FaCalendarAlt className="text-[10px]" />
                <span>{step.duration}</span>
              </div>
              <button 
                onClick={() => setModalOpen(step.modalId)}
                className="w-full py-1.5 text-xs font-medium text-blue-600 bg-blue-50 rounded-lg group-hover:bg-blue-600 group-hover:text-white transition-all duration-300"
              >
                {step.action}
              </button>
            </div>
          ))}
        </div>

        {/* Apply Now Modal */}
        <Modal isOpen={modalOpen === 'apply'} onClose={() => setModalOpen(null)} title="Apply for Admission 2025-26">
          <form onSubmit={handleApplySubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
              <div className="relative">
                <FaUser className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
                <input
                  type="text"
                  name="fullName"
                  required
                  value={formData.fullName}
                  onChange={handleInputChange}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  placeholder="Enter your full name"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email Address *</label>
              <div className="relative">
                <MdEmail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
                <input
                  type="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  placeholder="you@example.com"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Mobile Number *</label>
              <div className="relative">
                <MdPhone className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
                <input
                  type="tel"
                  name="phone"
                  required
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  placeholder="9876543210"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Select Course *</label>
              <select
                name="course"
                required
                value={formData.course}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
              >
                <option value="">-- Select Course --</option>
                {courses.map(course => (
                  <option key={course} value={course}>{course}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Highest Qualification *</label>
              <input
                type="text"
                name="qualification"
                required
                value={formData.qualification}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                placeholder="e.g., 12th Pass, Graduation"
              />
            </div>
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-gradient-to-r from-blue-600 to-cyan-500 text-white py-2 rounded-lg font-semibold hover:shadow-lg transition-all disabled:opacity-70 flex items-center justify-center gap-2"
            >
              {isSubmitting ? <FaSpinner className="animate-spin" /> : <FaRocket />}
              {isSubmitting ? 'Submitting...' : 'Submit Application'}
            </button>
          </form>
        </Modal>

        {/* Upload Now Modal */}
        <Modal isOpen={modalOpen === 'upload'} onClose={() => setModalOpen(null)} title="Upload Documents">
          <form onSubmit={handleUploadSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">10th Marksheet *</label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-3 text-center hover:border-blue-500 transition-colors">
                <input
                  type="file"
                  accept=".pdf,.jpg,.png"
                  onChange={(e) => handleFileChange(e, 'marksheet10')}
                  className="w-full text-sm text-gray-500 file:mr-2 file:py-1 file:px-3 file:rounded-lg file:border-0 file:text-sm file:bg-blue-50 file:text-blue-600 hover:file:bg-blue-100"
                  required
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">12th Marksheet *</label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-3 text-center hover:border-blue-500 transition-colors">
                <input
                  type="file"
                  accept=".pdf,.jpg,.png"
                  onChange={(e) => handleFileChange(e, 'marksheet12')}
                  className="w-full text-sm text-gray-500 file:mr-2 file:py-1 file:px-3 file:rounded-lg file:border-0 file:text-sm file:bg-blue-50 file:text-blue-600 hover:file:bg-blue-100"
                  required
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Recent Photograph *</label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-3 text-center hover:border-blue-500 transition-colors">
                <input
                  type="file"
                  accept=".jpg,.png"
                  onChange={(e) => handleFileChange(e, 'photo')}
                  className="w-full text-sm text-gray-500 file:mr-2 file:py-1 file:px-3 file:rounded-lg file:border-0 file:text-sm file:bg-blue-50 file:text-blue-600 hover:file:bg-blue-100"
                  required
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Signature *</label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-3 text-center hover:border-blue-500 transition-colors">
                <input
                  type="file"
                  accept=".jpg,.png"
                  onChange={(e) => handleFileChange(e, 'signature')}
                  className="w-full text-sm text-gray-500 file:mr-2 file:py-1 file:px-3 file:rounded-lg file:border-0 file:text-sm file:bg-blue-50 file:text-blue-600 hover:file:bg-blue-100"
                  required
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-gradient-to-r from-green-500 to-emerald-500 text-white py-2 rounded-lg font-semibold hover:shadow-lg transition-all disabled:opacity-70 flex items-center justify-center gap-2"
            >
              {isSubmitting ? <FaSpinner className="animate-spin" /> : <FaUpload />}
              {isSubmitting ? 'Uploading...' : 'Upload Documents'}
            </button>
          </form>
        </Modal>

        {/* Pay Now Modal */}
        <Modal isOpen={modalOpen === 'pay'} onClose={() => setModalOpen(null)} title="Pay Application Fee">
          <form onSubmit={handlePaySubmit} className="space-y-4">
            <div className="bg-blue-50 p-4 rounded-lg text-center mb-2">
              <p className="text-sm text-gray-600">Application Fee Amount</p>
              <p className="text-2xl font-bold text-blue-600">₹500/-</p>
              <p className="text-xs text-gray-500">(Non-refundable)</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Application ID / Mobile Number *</label>
              <input
                type="text"
                name="applicationId"
                required
                value={formData.applicationId}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                placeholder="Enter Application ID or Registered Mobile No"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Select Payment Method *</label>
              <div className="space-y-2">
                {['Credit/Debit Card', 'Net Banking', 'UPI', 'Wallet'].map(method => (
                  <label key={method} className="flex items-center gap-2 p-2 border rounded-lg cursor-pointer hover:bg-gray-50">
                    <input
                      type="radio"
                      name="paymentMethod"
                      value={method}
                      checked={formData.paymentMethod === method}
                      onChange={handleInputChange}
                      className="text-blue-600"
                      required
                    />
                    <span className="text-sm text-gray-700">{method}</span>
                  </label>
                ))}
              </div>
            </div>
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-gradient-to-r from-orange-500 to-red-500 text-white py-2 rounded-lg font-semibold hover:shadow-lg transition-all disabled:opacity-70 flex items-center justify-center gap-2"
            >
              {isSubmitting ? <FaSpinner className="animate-spin" /> : <FaCreditCard />}
              {isSubmitting ? 'Processing...' : 'Pay ₹500 Now'}
            </button>
          </form>
        </Modal>

        {/* Check Status Modal */}
        <Modal isOpen={modalOpen === 'status'} onClose={() => { setModalOpen(null); setStatusResult(null); }} title="Check Application Status">
          {!statusResult ? (
            <form onSubmit={handleStatusSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Application ID *</label>
                <input
                  type="text"
                  name="applicationId"
                  required
                  value={formData.applicationId}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  placeholder="Enter your Application ID"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Registered Mobile Number *</label>
                <input
                  type="tel"
                  name="mobileNumber"
                  required
                  value={formData.mobileNumber}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  placeholder="9876543210"
                />
              </div>
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-blue-400 to-blue-500 text-white py-2 rounded-lg font-semibold hover:shadow-lg transition-all disabled:opacity-70 flex items-center justify-center gap-2"
              >
                {isSubmitting ? <FaSpinner className="animate-spin" /> : <FaCheckCircle />}
                {isSubmitting ? 'Checking...' : 'Check Status'}
              </button>
            </form>
          ) : (
            <div className="space-y-4">
              <div className={`p-4 rounded-lg text-center ${
                statusResult.status === 'Approved' ? 'bg-green-50' : 
                statusResult.status === 'Rejected' ? 'bg-red-50' : 'bg-yellow-50'
              }`}>
                <FaCheckCircle className={`text-3xl mx-auto mb-2 ${
                  statusResult.status === 'Approved' ? 'text-green-500' : 
                  statusResult.status === 'Rejected' ? 'text-red-500' : 'text-yellow-500'
                }`} />
                <p className="font-bold text-lg">Status: {statusResult.status}</p>
                <p className="text-sm text-gray-600 mt-2">{statusResult.message}</p>
              </div>
              <div className="border-t pt-3 space-y-2">
                <p className="text-sm"><strong>Application ID:</strong> {statusResult.applicationId}</p>
                <p className="text-sm"><strong>Name:</strong> {statusResult.name}</p>
                <p className="text-sm"><strong>Course:</strong> {statusResult.course}</p>
                <p className="text-sm"><strong>Applied On:</strong> {statusResult.date}</p>
              </div>
              <button
                onClick={() => { setModalOpen(null); setStatusResult(null); }}
                className="w-full bg-gray-100 text-gray-700 py-2 rounded-lg font-semibold hover:bg-gray-200 transition-colors"
              >
                Close
              </button>
            </div>
          )}
        </Modal>

        {/* Two Column Layout */}
        <div className={`grid md:grid-cols-2 gap-6 mb-10 transition-all duration-700 delay-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          
          {/* Left Side - Requirements */}
          <div className="bg-white rounded-xl p-5 shadow-md border border-gray-100">
            <div className="flex items-center gap-2 mb-4">
              <GiBookCover className="text-blue-600 text-xl" />
              <h3 className="text-lg font-bold text-gray-800">Required Documents</h3>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {requirements.map((req, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <req.icon className={`${req.color} text-xs`} />
                  <span className="text-xs text-gray-600">{req.text}</span>
                </div>
              ))}
            </div>
            <div className="mt-4 pt-3 border-t border-gray-100">
              <p className="text-xs text-gray-500">*Self-attested copies required</p>
            </div>
          </div>

          {/* Right Side - Important Dates */}
          <div className="bg-white rounded-xl p-5 shadow-md border border-gray-100">
            <div className="flex items-center gap-2 mb-4">
              <FaCalendarAlt className="text-blue-600 text-xl" />
              <h3 className="text-lg font-bold text-gray-800">Important Dates</h3>
            </div>
            <div className="space-y-3">
              {dates.map((item, idx) => (
                <div key={idx} className="flex items-center justify-between pb-2 border-b border-gray-100 last:border-0">
                  <div>
                    <p className="text-sm font-medium text-gray-700">{item.label}</p>
                    <p className="text-xs text-gray-500">{item.date}</p>
                  </div>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                    item.status === 'Open' ? 'bg-green-100 text-green-600' : 'bg-yellow-100 text-yellow-600'
                  }`}>
                    {item.status}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Contact Support */}
        <div className={`text-center mt-6 transition-all duration-700 delay-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <p className="text-xs text-gray-500">
            Need help? Call us: 
            or email: 
            <a href="mailto:admissions@rdsgroupofcolleges.com" className="text-blue-600 font-semibold ml-1">admissions@rdsgroupofcolleges.com</a>
          </p>
        </div>
      </div>

      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from { transform: translateY(20px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        .animate-fadeIn {
          animation: fadeIn 0.2s ease-out;
        }
        .animate-slideUp {
          animation: slideUp 0.3s ease-out;
        }
      `}</style>
    </section>
  );
};

export default AdmissionSection;